import * as z from 'zod';

import { PaymentAddress } from '../Address';
import { BorrowerId, EscrowSettingId, PayeeId, PropertyId } from '../BrandedIds';
import { zodJSInstantDate, zodJSPlainDate } from '../dates/BrandedDate';
import { OptionalPhoneNumber } from '../Phone';
import { Temporal } from '../Temporal';
import { DueMonthDay } from '../utils/Zod';
import { OptionalEmailString } from '../validations/email';
import { NonNegativeMonetaryValue } from '../validations/fields';

export const PaymentCadence = z.enum(['ANNUAL', 'MONTHLY', 'SEMI-ANNUAL', 'QUARTERLY', 'UPFRONT']);

export type PaymentCadence = z.infer<typeof PaymentCadence>;

export const PaymentCadenceEnum = z.preprocess((val) => {
  if (typeof val === 'string') {
    return val.toUpperCase();
  }
  return val;
}, PaymentCadence);

export type PaymentCadenceEnum = z.infer<typeof PaymentCadenceEnum>;

export const EscrowSettingStatus = z.enum(['ACTIVE', 'INACTIVE']);

export const LegacyEscrowPayee = z.object({
  name: z.string(),
  address: PaymentAddress.optional(),
});
export type LegacyEscrowPayee = z.infer<typeof LegacyEscrowPayee>;

export const BaseEscrowSetting = z.object({
  id: EscrowSettingId,
  propertyId: PropertyId,
  escrowed: z.boolean(),
  status: EscrowSettingStatus,
  amount: NonNegativeMonetaryValue,
  /**
   * @deprecated Make sure you resolve with payee table if it exists
   **/
  payee: LegacyEscrowPayee,
  payeeId: PayeeId.optional(),
  propertyIndex: z.number().optional(), // Used to pair with the right property
  updatedAt: zodJSInstantDate,
});

export const TaxEscrowType = z.enum(['COUNTY_TAX', 'CITY_TAX', 'SCHOOL_TAX', 'TOWN_TAX', 'VILLAGE_TAX', 'OTHER_TAX']);

export type TaxEscrowType = z.infer<typeof TaxEscrowType>;

export const TaxEscrowSetting = BaseEscrowSetting.extend({
  type: z.literal('taxEscrowSetting'),
  dueDates: z.array(DueMonthDay).optional(),
  taxType: TaxEscrowType,
  apn: z.string().optional(),
  contractNumber: z.string().optional(),
});

export type TaxEscrowSetting = z.infer<typeof TaxEscrowSetting>;

export const EscrowInsuranceType = z.enum([
  'hazard',
  'homeowners',
  'flood',
  'windstorm',
  'hurricane',
  'fire',
  'liability',
  'rental',
  'condo',
  'earthquake',
  'umbrella',
  'sinkhole',
  'wildfire',
  'sewer',
  'equipment',
  'title',
  'other',
]);

export type EscrowInsuranceType = z.infer<typeof EscrowInsuranceType>;

// TODO remove this once we've migrated all insurance settings
export const CoercedEscrowInsuranceType = z
  .string()
  .optional()
  .transform((v) => {
    const normalizedValue = (v ?? '')
      .toLowerCase()
      .replace(/insurance/g, '')
      .replace(/ /g, '');
    return normalizedValue;
  })
  .pipe(EscrowInsuranceType);

export const InsuranceEscrowSetting = BaseEscrowSetting.extend({
  type: z.literal('insuranceEscrowSetting'),
  insuranceName: z.string().optional(), // Deprecate
  insuranceType: CoercedEscrowInsuranceType.optional(), // Make required
  agentName: z.string().optional(),
  agentEmail: OptionalEmailString,
  agentPhone: OptionalPhoneNumber,
  policyNumber: z.string().optional(),
  policyStartDate: zodJSPlainDate.optional(),
  policyEndDate: zodJSPlainDate,
  coverageAmount: NonNegativeMonetaryValue.optional(),
  forcedPlacedFlag: z.boolean().optional(),
});

export type InsuranceEscrowSetting = z.infer<typeof InsuranceEscrowSetting>;

export const isInsuranceEscrowSetting = (setting: EscrowSetting): setting is InsuranceEscrowSetting =>
  setting.type === 'insuranceEscrowSetting';

export const TaxEscrowSettingInput = TaxEscrowSetting.omit({ propertyId: true, updatedAt: true }).extend({
  county: z.string().optional(),
  propertyId: PropertyId.optional(),
});

export type TaxEscrowSettingInput = z.infer<typeof TaxEscrowSettingInput>;

export const InsuranceEscrowSettingInput = InsuranceEscrowSetting.omit({ propertyId: true, updatedAt: true }).extend({
  propertyId: PropertyId.optional(),
});

export type InsuranceEscrowSettingInput = z.infer<typeof InsuranceEscrowSettingInput>;

export const MortgageInsuranceType = z.enum(['PMI', 'UFMIP', 'FHA']);

export type MortgageInsuranceType = z.infer<typeof MortgageInsuranceType>;

export const MortgageInsuranceTypeEnum = z.preprocess((val) => {
  if (typeof val === 'string') {
    return val.toUpperCase();
  }
  return val;
}, MortgageInsuranceType);

export type MortgageInsuranceTypeEnum = z.infer<typeof MortgageInsuranceTypeEnum>;

export const MortgageInsuranceFHAProgramType = z.enum(['203B', '203K', '234C']);

export type MortgageInsuranceFHAProgramType = z.infer<typeof MortgageInsuranceFHAProgramType>;

export const MortgageInsuranceFHAProgramTypeEnum = z.preprocess((val) => {
  if (typeof val === 'string') {
    return val.toUpperCase();
  }
  return val;
}, MortgageInsuranceFHAProgramType);

export type MortgageInsuranceFHAProgramTypeEnum = z.infer<typeof MortgageInsuranceFHAProgramTypeEnum>;

export const MortgageInsuranceRenewalType = z.enum(['CONSTANT', 'DECLINING']);

export type MortgageInsuranceRenewalType = z.infer<typeof MortgageInsuranceRenewalType>;

export const MortgageInsuranceRenewalTypeEnum = z.preprocess((val) => {
  if (typeof val === 'string') {
    return val.toUpperCase();
  }
  return val;
}, MortgageInsuranceRenewalType);
export type MortgageInsuranceRenewalTypeEnum = z.infer<typeof MortgageInsuranceRenewalTypeEnum>;

export const MortgageInsuranceDiscontinuanceCode = z.enum([
  'borrowerInitiatedDueToOriginalValue',
  'borrowerInitiatedDueToCurrentValue',
  'automaticDueToLTVOrMidpointValue',
  'terminatedDueToHighRisk',
  'shouldNotHaveBeenApplied',
]);
export type MortgageInsuranceDiscontinuanceCode = z.infer<typeof MortgageInsuranceDiscontinuanceCode>;

export const MortgageInsuranceCancellationEligibilityReason = z.enum([
  'originalLTV',
  'currentLTV',
  'midpointDate',
  'mipLTVThresholdDate',
]);
export type MortgageInsuranceCancellationEligibilityReason = z.infer<
  typeof MortgageInsuranceCancellationEligibilityReason
>;

export const MORTGAGE_INSURANCE_DISCONTINUANCE_CODE_DESCRIPTIONS: Record<MortgageInsuranceDiscontinuanceCode, string> =
  {
    borrowerInitiatedDueToOriginalValue: 'Borrower initiated due to original value',
    borrowerInitiatedDueToCurrentValue: 'Borrower initiated due to current value',
    automaticDueToLTVOrMidpointValue: 'Automatic due to LTV or midpoint value',
    terminatedDueToHighRisk: 'Terminated due to high risk',
    shouldNotHaveBeenApplied: 'Cancel (should not have been applied)',
  };

export const MortgageInsuranceCancellationNotification = z.enum(['standard', 'none']);
export type MortgageInsuranceCancellationNotification = z.infer<typeof MortgageInsuranceCancellationNotification>;

export const MORTGAGE_INSURANCE_CANCELLATION_NOTIFICATION_DESCRIPTIONS: Record<
  MortgageInsuranceCancellationNotification,
  string
> = {
  standard: 'Cancellation confirmation (standard)',
  none: 'Do not send a notification',
};

export const MortgageInsuranceCancellation = z.object({
  cancellationEffectiveDate: zodJSPlainDate,
  // TODO: remove this default once we've migrated all escrow settings to have a cancellationDiscontinuanceCode
  cancellationDiscontinuanceCode: MortgageInsuranceDiscontinuanceCode.optional().default('terminatedDueToHighRisk'),
  // Absent on settings cancelled before the notification choice existed; treated as 'standard'
  cancellationNotification: MortgageInsuranceCancellationNotification.optional(),
  // Absent on settings cancelled before recipients were selectable; treated as primary borrower only
  notificationRecipientIds: z.array(BorrowerId).optional(),
});

export const MortgageInsuranceCancellationInput = MortgageInsuranceCancellation.extend({
  escrowSettingId: EscrowSettingId,
  // The persisted schema defaults this for legacy data; new cancellations must choose explicitly
  cancellationDiscontinuanceCode: z.enum(MortgageInsuranceDiscontinuanceCode.options, { error: 'Required' }),
  cancellationNotification: MortgageInsuranceCancellationNotification,
  notificationRecipientIds: z.array(BorrowerId),
}).superRefine(({ cancellationEffectiveDate }, ctx) => {
  const cancellationEffectiveDatePlainDate = Temporal.PlainDate.from(
    cancellationEffectiveDate.toISOString().split('T')[0],
  );
  if (Temporal.PlainDate.compare(cancellationEffectiveDatePlainDate, Temporal.Now.plainDateISO('UTC')) > 0) {
    ctx.addIssue({
      code: 'custom',
      message: 'Cancellation effective date must be on or before today',
      path: ['cancellationEffectiveDate'],
    });
  }
});
export type MortgageInsuranceCancellationInput = z.infer<typeof MortgageInsuranceCancellationInput>;

export const MortgageInsuranceEscrowSetting = BaseEscrowSetting.extend({
  type: z.literal('mortgageInsuranceEscrowSetting'),
  mortgageInsuranceType: MortgageInsuranceType,
  fhaProgramType: MortgageInsuranceFHAProgramType.optional(),
  startDate: zodJSPlainDate,
  midpointDate: zodJSPlainDate.optional(),
  paymentCadence: PaymentCadence,
  certificateId: z.string(),
  poolCertificateNumber: z.string().optional(),
  coveragePercent: z.number().nonnegative(), // Number between 0 and 1
  isRefundable: z.boolean(),
  isFinanced: z.boolean().optional(),
  renewalType: MortgageInsuranceRenewalType,
  cancellationDetails: MortgageInsuranceCancellation.nullable().optional(),
});
export type MortgageInsuranceEscrowSetting = z.infer<typeof MortgageInsuranceEscrowSetting>;

export const isMortgageInsuranceEscrowSetting = (setting: EscrowSetting): setting is MortgageInsuranceEscrowSetting =>
  setting.type === 'mortgageInsuranceEscrowSetting';

export const EscrowSetting = z.discriminatedUnion('type', [
  TaxEscrowSetting,
  InsuranceEscrowSetting,
  MortgageInsuranceEscrowSetting,
]);
export type EscrowSetting = z.infer<typeof EscrowSetting>;

export const ESCROW_SETTING_TYPE_LABELS: Record<EscrowSetting['type'], string> = {
  taxEscrowSetting: 'Tax',
  insuranceEscrowSetting: 'Insurance',
  mortgageInsuranceEscrowSetting: 'Mortgage Insurance',
};

export const ESCROW_SETTING_TYPE_TO_PAYEE_TYPE: Record<
  EscrowSetting['type'],
  'TAX' | 'INSURANCE' | 'MORTGAGE_INSURANCE'
> = {
  taxEscrowSetting: 'TAX',
  insuranceEscrowSetting: 'INSURANCE',
  mortgageInsuranceEscrowSetting: 'MORTGAGE_INSURANCE',
};

export const MortgageInsuranceSettingInput = MortgageInsuranceEscrowSetting.omit({ propertyId: true, updatedAt: true });
export type MortgageInsuranceSettingInput = z.infer<typeof MortgageInsuranceSettingInput>;

export const EscrowSettingInput = z.discriminatedUnion('type', [
  TaxEscrowSettingInput,
  InsuranceEscrowSettingInput,
  MortgageInsuranceSettingInput,
]);

export type EscrowSettingInput = z.infer<typeof EscrowSettingInput>;
